import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';

interface UserData {
  id: number;
  nome: string;
  email_contato: string;
  nivel_acesso: string;
  tipo_usuario: 'ADMIN' | 'ASSESSOR';
  grupo_politico_id: number;
  ativo: boolean;
  foto_url: string | null;
  papeis: string[];
  grupo_nome: string | null;
  grupo_cor_primaria: string | null;
  grupo_cor_primaria_rgb: string | null;
  grupo_logo_url: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userData: UserData | null;
  loading: boolean;
  isAdmin: boolean;
  isPasswordRecovery: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  clearPasswordRecovery: () => void;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  const fetchUserData = async (userId: string, token: string) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setUserData(result.data);
          setIsAdmin(result.data.tipo_usuario === 'ADMIN');
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
    }
  };

  const refreshUserData = async () => {
    if (session?.access_token && user?.id) {
      await fetchUserData(user.id, session.access_token);
    }
  };

  useEffect(() => {
    // Get initial session — only update state here; INITIAL_SESSION event
    // from onAuthStateChange below will call fetchUserData once.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (!session) {
        setLoading(false);
      }
      // Do NOT call fetchUserData here — onAuthStateChange fires
      // INITIAL_SESSION which already does it, avoiding a double call.
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }
      // Only fetch user data on sign-in events, not on TOKEN_REFRESHED / USER_UPDATED
      // to avoid an infinite loop of API calls every time Supabase refreshes the token.
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        if (session?.user && session.access_token) {
          fetchUserData(session.user.id, session.access_token);
        }
      } else if (event === 'SIGNED_OUT') {
        setUserData(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/configuracoes`,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  };

  const clearPasswordRecovery = () => {
    setIsPasswordRecovery(false);
  };

  const value = {
    user,
    session,
    userData,
    loading,
    isAdmin,
    isPasswordRecovery,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    clearPasswordRecovery,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
